/**
 * m2m mixin
 */

import { getName, indexIn, remove } from 'app/helpers/entity'

/**
 * m2mMixinFactory
 * returns m2mMixin mixin factory
 *
 * @class m2mMixinFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.m2mMixin} mixin factory
 *
 * @ngdoc factory
 * @name mwApp.factory:m2mMixinFactory
 */
/* @ngInject */
export default function m2mMixinFactory (Entity) {
  /* === public === */

  /**
   * Mixin with common functions to work with m2m fields
   *
   * @mixin m2mMixin
   * @memberof models
   * @see models
   * @param {String} field - field name for generated mixin
   * @return {Object}
   */
  function m2mMixin (field) {
    var plural = field.charAt(0).toUpperCase() + field.slice(1) // capitalize first letter
    var singular = plural
      .replace(/ies$/, 'y') // replace last 'ies' to 'y'
      .replace(/s$/, '') // or remove last 's'
    var mixin = {}

    /**
     * Add Smth to Field
     *
     * @memberof models.m2mMixin#
     * @method _add[Fieldname(singular)]
     * @example ._addNetwork() or ._addExtension()
     * @param {*}        what  - Smth to add
     * @param {Object[]} [src] - source array, if exists - remove Smth from it
     */

    // add method to add Smth to Field
    // like '_addNetwork' or '_addExtension'
    mixin['_add' + singular] = function (what, src) {
      addToField(this, field, what, src)
    }

    /**
     * Remove Smth from Field
     *
     * @memberof models.m2mMixin#
     * @method _remove[Fieldname(singular)]
     * @example ._removeNetwork() or ._removeExtension()
     * @param {*}        what  - Smth to remove
     * @param {Object[]} [dst] - destination array, if exists - add removed Smth from it
     */

    // add method to remove Smth from Field
    // like '_removeNetwork' or '_removeExtension'
    mixin['_remove' + singular] = function (what, dst) {
      removeFromField(this, field, what, dst)
    }

    /**
     * Reset Field
     *
     * @memberof models.m2mMixin#
     * @method _reset[Fieldname(plural)]
     * @example ._resetNetworks() or ._resetExtensions()
     * @param {Object}   self  - Entity
     * @param {String}   field - field name
     * @param {Object[]} [dst] - destination array, if exists - add removed Smth from it
     */

    // add method to reset Field
    // like '_resetNetworks' or '_resetExtensions'
    mixin['_reset' + plural] = function (dst) {
      resetField(this, field, dst)
    }

    /**
     * Get names from field
     *
     * @memberof models.m2mMixin#
     * @method _[fieldname(plural)]Names
     * @example ._networksNames() or ._extensionsNames()
     * @return {String[]}
     */

    // add method to get names from Field
    // like '_networksNames' or '_extensionsNames'
    mixin['_' + field + 'Names'] = function () {
      if (this[field] && this[field].length > 0) {
        return this[field].map(getName)
      }
      return []
    }

    return mixin
  }

  /* === private === */

  /**
   * Add Smth to Field
   *
   * @memberof models.m2mMixin#
   * @private
   * @param {Object}   self  - Entity
   * @param {String}   field - field name
   * @param {*}        what  - Smth to add
   * @param {Object[]} [src] - source array, if exists - remove Smth from it
   */
  function addToField (self, field, what, src) {
    if (what === null || what === undefined) {
      return
    }

    var index = -1
    if (!self[field]) {
      self[field] = []
    } else {
      index = indexIn(self[field])(what)
    }
    if (index === -1) {
      self[field].push(what)
      if (src !== undefined) {
        remove(src)(what)()
      }
    }
  }

  /**
   * Remove Smth from Field
   *
   * @memberof models.m2mMixin#
   * @private
   * @param {Object}   self  - Entity
   * @param {String}   field - field name
   * @param {*}        what  - Smth to remove
   * @param {Object[]} [dst] - destination array, if exists - add removed Smth from it
   */
  function removeFromField (self, field, what, dst) {
    if (what === null || what === undefined) {
      return
    }

    remove(self[field])(what)()
    if (dst !== undefined) {
      dst.push(what)
    }
  }

  /**
   * Reset Field
   *
   * @memberof models.m2mMixin#
   * @private
   * @param {Object}   self  - Entity
   * @param {String}   field - field name
   * @param {Object[]} [dst] - destination array, if exists - add removed Smth from it
   */
  function resetField (self, field, dst) {
    if (dst !== undefined) {
      // concat arrays without creating new object
      Array.prototype.push.apply(dst, self[field])
    }

    self[field].length = 0
  }

  return m2mMixin
}
